import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPlayers, Player } from "../../../api/players";
import { card, inputStyle, smallBtn, dangerBtn } from "../../../styles";

export interface GwPickerGameweek {
  id: string;
  number: number;
  completed: boolean;
}

interface LocalPick {
  playerId: string;
  playerName: string;
  playerClub: string | null;
}

export interface InitialGwPick {
  gameweekId: string;
  playerId: string;
  playerName: string | null;
  playerClub: string | null;
}

interface Props {
  user: User;
  competitionId: string;
  gameweeks: GwPickerGameweek[];
  initialPicks?: InitialGwPick[];
  onChange: (picks: { gameweekId: string; playerId: string }[]) => void;
}

/**
 * Admin-only picker for correcting a GAMEWEEK_CHALLENGE member's picks mid-season —
 * genuinely new, unlike the other three backfill/edit pickers, since GwChallengePage.tsx
 * is monolithic and exports no reusable picker (contrast SyndicatePage's PotCard or the
 * standalone FpDragRow). One row per gameweek in the competition; rows for an
 * already-completed gameweek are flagged, since editing one is the highest-consequence
 * action in this route (it retroactively changes a scored result). There is no
 * completeness bar — any subset of gameweeks, including none, is a valid submission,
 * mirroring the member route's "pick any gameweek independently" behaviour.
 *
 * Only used from the add-nothing edit flow (there is no GWC backfill on add-member —
 * see admin.ts), so this never needs to start pre-populated except via `initialPicks`.
 */
export default function GwChallengePicker({ user, competitionId, gameweeks, initialPicks, onChange }: Props) {
  const { t } = useTranslation();
  const [picks, setPicks] = useState<Record<string, LocalPick>>(() => {
    const seeded: Record<string, LocalPick> = {};
    for (const ip of initialPicks ?? []) {
      seeded[ip.gameweekId] = { playerId: ip.playerId, playerName: ip.playerName ?? ip.playerId, playerClub: ip.playerClub ?? null };
    }
    return seeded;
  });
  const [openGwId, setOpenGwId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Player[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!openGwId || !search.trim()) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    fetchPlayers(user, { competitionId, page: 1, pageSize: 10, search: search.trim() })
      .then((res) => { if (!cancelled) setResults(res.players); })
      .catch(() => { /* ignore — dropdown just shows no results */ })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [user, competitionId, openGwId, search]);

  const emit = (next: Record<string, LocalPick>) => {
    onChange(Object.entries(next).map(([gameweekId, pick]) => ({ gameweekId, playerId: pick.playerId })));
  };

  const openRow = (gwId: string) => {
    setOpenGwId((cur) => (cur === gwId ? null : gwId));
    setSearch("");
    setResults([]);
  };

  const choosePlayer = (gwId: string, player: Player) => {
    setPicks((prev) => {
      const next = { ...prev, [gwId]: { playerId: player.id, playerName: player.name, playerClub: player.club } };
      emit(next);
      return next;
    });
    setOpenGwId(null);
    setSearch("");
    setResults([]);
  };

  const clearRow = (gwId: string) => {
    setPicks((prev) => {
      const next = { ...prev };
      delete next[gwId];
      emit(next);
      return next;
    });
  };

  // Client-side mirror of the backend's duplicate check — surfaces the conflict at
  // pick-time instead of only after a rejected save. The backend remains authoritative.
  const duplicateOf = (playerId: string, excludingGwId: string): number | null => {
    for (const [gwId, pick] of Object.entries(picks)) {
      if (gwId !== excludingGwId && pick.playerId === playerId) {
        const gw = gameweeks.find((g) => g.id === gwId);
        return gw?.number ?? null;
      }
    }
    return null;
  };

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      {gameweeks.map((gw, i) => {
        const pick = picks[gw.id];
        const isOpen = openGwId === gw.id;
        return (
          <div
            key={gw.id}
            style={{
              padding: "0.7rem 1rem",
              borderTop: i === 0 ? undefined : "1px solid var(--color-border)",
              display: "flex", flexDirection: "column", gap: "0.4rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>GW{gw.number}</span>
                {gw.completed && (
                  <span style={{ fontSize: "0.7rem", padding: "1px 7px", borderRadius: 99, background: "var(--color-danger)", color: "#fff" }}>
                    {t("admin.leagues.detail.editSelections.gwc.completed")}
                  </span>
                )}
                <span style={{ fontSize: "0.85rem", color: pick ? "var(--color-text)" : "var(--color-text-muted)" }}>
                  {pick ? `${pick.playerName}${pick.playerClub ? ` — ${pick.playerClub}` : ""}` : t("admin.leagues.detail.editSelections.gwc.noPick")}
                </span>
              </div>
              <div style={{ display: "flex", gap: "0.4rem" }}>
                {pick && (
                  <button style={{ ...dangerBtn, padding: "0.3rem 0.6rem", fontSize: "0.75rem" }} onClick={() => clearRow(gw.id)}>
                    {t("admin.leagues.detail.editSelections.gwc.clear")}
                  </button>
                )}
                <button style={{ ...smallBtn, fontSize: "0.75rem" }} onClick={() => openRow(gw.id)}>
                  {t("admin.leagues.detail.editSelections.gwc.change")}
                </button>
              </div>
            </div>
            {isOpen && (
              <div style={{ position: "relative" }}>
                <input
                  autoFocus
                  style={{ ...inputStyle, fontSize: "0.85rem", width: "100%", maxWidth: 320 }}
                  placeholder={t("admin.leagues.detail.editSelections.gwc.searchPlaceholder") as string}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {(results.length > 0 || searching) && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, maxWidth: 320, background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "6px", zIndex: 10, maxHeight: 220, overflowY: "auto" }}>
                    {results.map((p) => {
                      const dupGw = duplicateOf(p.id, gw.id);
                      return (
                        <div
                          key={p.id}
                          onClick={() => { if (dupGw === null) choosePlayer(gw.id, p); }}
                          style={{
                            padding: "0.4rem 0.75rem", fontSize: "0.85rem",
                            cursor: dupGw === null ? "pointer" : "not-allowed",
                            color: dupGw === null ? "var(--color-text)" : "var(--color-text-muted)",
                          }}
                        >
                          {p.name}{p.club ? ` — ${p.club}` : ""}
                          {dupGw !== null && (
                            <span style={{ display: "block", fontSize: "0.72rem", color: "var(--color-danger)" }}>
                              {t("admin.leagues.detail.editSelections.gwc.duplicatePlayer", { name: p.name, number: dupGw })}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
